import { useState, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { LockedFeature } from "../plan/PlanUI";
import { CHAT } from "../../constants/testIds";
import { X, Sparkles, Trash2 } from "lucide-react";

export function ExamModeModal({ open, onClose, current, onSaved }) {
  const { user } = useAuth();
  const locked = (user?.plano || "gratis") === "gratis";
  const [subject, setSubject] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (current) {
      setSubject(current.subject || "");
      setTitle(current.title || "");
      setDate(current.date?.slice(0, 10) || "");
      setContent(current.content || "");
    } else {
      setSubject(""); setTitle(""); setDate(""); setContent("");
    }
  }, [current, open]);

  if (!open) return null;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("/exam-mode", { subject, title, date, content });
      onSaved();
      onClose();
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete("/exam-mode");
      onSaved();
      onClose();
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div data-testid={CHAT.examModal} className="w-full sm:max-w-lg bg-card border border-border rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-400" />
            <h2 className="font-bold tracking-tight">Modo Prova</h2>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
        {locked ? (
          <LockedFeature
            titulo="Modo Prova"
            descricao="Trava o assistente 100% no conteúdo EXATO da sua prova — chat, quiz e flashcards param de abranger e focam só no que vai cair."
            planoNecessario="medio"
          />
        ) : (
        <>
        <div className="flex-1 overflow-y-auto p-5 space-y-3 scrollbar-thin">
          <p className="text-xs text-muted-foreground">
            Trava todo o assistente na matéria EXATA dessa prova. Quiz, flashcards e chat passam a girar em torno deste conteúdo.
          </p>
          <Field label="Matéria">
            <input
              data-testid={CHAT.examSubjectInput}
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Matemática"
              className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50"
            />
          </Field>
          <Field label="Título da prova">
            <input
              data-testid={CHAT.examTitleInput}
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Trimestral de Matemática"
              className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50"
            />
          </Field>
          <Field label="Data">
            <input
              data-testid={CHAT.examDateInput}
              type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50"
            />
          </Field>
          <Field label="Conteúdo exato que vai cair">
            <textarea
              data-testid={CHAT.examContentInput}
              value={content} onChange={(e) => setContent(e.target.value)}
              rows={8}
              placeholder="Cola aqui o roteiro / lista de exercícios / tópicos."
              className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 resize-none scrollbar-thin"
            />
          </Field>
          {err && <p className="text-sm text-red-400">{err}</p>}
        </div>
        <div className="p-4 border-t border-border flex gap-2 items-center">
          {current && (
            <button
              data-testid={CHAT.examClearButton}
              onClick={clear} disabled={busy}
              className="rounded-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" /> Desativar
            </button>
          )}
          <div className="flex-1" />
          <button
            data-testid={CHAT.examSaveButton}
            onClick={save} disabled={busy || !subject || !title || !date}
            className="rounded-full bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Salvando..." : current ? "Atualizar prova" : "Ativar Modo Prova"}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</span>
      {children}
    </label>
  );
}
